import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ui/loader";
import { Mail, Phone } from "lucide-react";
import { helpAPI } from "@/lib/api";

/** Uses the existing support directory; changing subscriptions remains a platform-admin operation. */
export default function ChangePlanDialog() {
	const [open, setOpen] = useState(false);
	const { t, i18n } = useTranslation();
	const [contact, setContact] = useState<{ email?: string; phone?: string; phone2?: string } | null>(null);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);
	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		if (!open) return;
		let cancelled = false;
		setLoading(true);
		setFailed(false);
		setContact(null);
		helpAPI
			.getContactInfo(i18n.language)
			.then(data => {
				if (!cancelled) setContact(data);
			})
			.catch(() => {
				if (!cancelled) setFailed(true);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [open, i18n.language, attempt]);
	const phones = [contact?.phone, contact?.phone2].filter(
		(phone, index, all) => phone && all.indexOf(phone) === index,
	);
	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button>{t("subscription.changePlan")}</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t("subscription.changePlan")}</DialogTitle>
					<DialogDescription>{t("subscription.changePlanHelp")}</DialogDescription>
				</DialogHeader>
				{loading ? (
					<Loading />
				) : failed ? (
					<div role="alert" className="space-y-3">
						<p className="text-sm">{t("subscription.loadError")}</p>
						<Button variant="outline" onClick={() => setAttempt(value => value + 1)}>
							{t("common.retry")}
						</Button>
					</div>
				) : (
					<div className="space-y-3">
						{contact?.email && (
							<a
								className="flex min-h-12 items-center gap-3 rounded-lg border border-border p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
								href={`mailto:${contact.email}?subject=${encodeURIComponent(t("subscription.changePlan"))}`}
							>
								<Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
								<span className="min-w-0 break-all" dir="ltr">
									{contact.email}
								</span>
							</a>
						)}
						{phones.map(phone => (
							<a
								key={phone}
								className="flex min-h-12 items-center gap-3 rounded-lg border border-border p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
								href={`tel:${phone}`}
							>
								<Phone className="h-4 w-4 shrink-0" aria-hidden="true" />
								<span dir="ltr">{phone}</span>
							</a>
						))}
						{!contact?.email && phones.length === 0 && (
							<p className="text-sm text-muted-foreground">{t("subscription.contactUnavailable")}</p>
						)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
